import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { TenantSettingsCard } from "@/components/TenantSettingsCard";
import { RecentSettingChangesCard } from "@/components/RecentSettingChangesCard";
import { DangerZoneCard } from "@/components/DangerZoneCard";
import AlvysSettingsCard from "@/components/AlvysSettingsCard";  // Phase AL0
import FmcsaSettingsCard from "@/components/FmcsaSettingsCard";  // Phase QCM
import PwaStatusCard from "@/components/PwaStatusCard";  // Phase PWA
import InvoiceCategoriesCard from "@/components/InvoiceCategoriesCard";  // Phase INV-CAT
import { DefaultDepositsCard } from "@/components/DefaultDepositsCard";  // Task 17 — Zero-deposit guardrails
import CoiRefreshCard from "@/components/CoiRefreshCard";  // Phase COI-REFRESH (Part B)
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronDown, RefreshCcw } from "lucide-react";
import {
    Settings,
    Mail,
    FileSignature,
    Truck,
    ShieldCheck,
    AlertTriangle,
    History,
    Send,
    KeyRound,
    Trash2,
    Receipt,
    Clock,
    CalendarClock,
    Sparkles,
} from "lucide-react";

// Phase S1 — accordion section schema.
const SECTION_DEFS = [
    { slug: "identity-branding",   title: "Identity & Branding",
      description: "Tenant identity, default contract / invoice templates." },
    { slug: "integrations",        title: "Integrations",
      description: "Stripe, Resend, mock-email mode, and the AI template generator." },
    { slug: "notifications-email", title: "Notifications & Email",
      description: "Per-tenant notification defaults (mock-emails viewer coming soon)." },
    { slug: "audit-activity",      title: "Audit & Activity",
      description: "Settings audit trail with secrets masked to last 4 chars." },
    { slug: "operational-tools",   title: "Operational Tools",
      description: "Cron cadence overrides, invite expiry, COI defaults." },
    { slug: "danger-zone",         title: "Danger Zone",
      description: "Backups and destructive factory reset. Super admin only.",
      superAdminOnly: true },
];
const SECTION_STORAGE_KEY = "admin-system-tab-sections-v1";

function defaultSectionState(tenantConfigured) {
    // Smart default on first visit: open Identity & Branding ONLY if the
    // tenant doc is still the RentFlow fallback. Otherwise all closed.
    return SECTION_DEFS.reduce((acc, s) => {
        acc[s.slug] = s.slug === "identity-branding" && !tenantConfigured;
        return acc;
    }, {});
}

function readSectionStateFromStorage() {
    try {
        const raw = localStorage.getItem(SECTION_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Sanity check — must be an object whose keys are known slugs.
        if (!parsed || typeof parsed !== "object") return null;
        const out = {};
        for (const def of SECTION_DEFS) {
            out[def.slug] = parsed[def.slug] === true;
        }
        return out;
    } catch {
        return null;
    }
}

function writeSectionStateToStorage(state) {
    try {
        localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* localStorage full / private mode — ignore */
    }
}

function SystemAccordionSection({ section, open, onToggle, children }) {
    const slug = section.slug;
    return (
        <section
            className="rounded-xl border border-slate-200 bg-white overflow-hidden"
            data-testid={`system-section-${slug}`}
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
                data-testid={`system-section-${slug}-toggle`}
            >
                <div className="text-left">
                    <h2 className="font-display text-lg font-semibold text-slate-900">
                        {section.title}
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">{section.description}</p>
                </div>
                <ChevronDown
                    size={18}
                    className={`shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
                    data-testid={`system-section-${slug}-chevron`}
                />
            </button>
            {open && (
                <div
                    className="px-5 pb-6 pt-1 grid grid-cols-1 lg:grid-cols-2 gap-6"
                    data-testid={`system-section-${slug}-content`}
                >
                    {children}
                </div>
            )}
        </section>
    );
}


const SAFER_HOUR_OPTIONS = [
    { value: 6, label: "Every 6 hours" },
    { value: 12, label: "Every 12 hours" },
    { value: 24, label: "Daily (24h)" },
    { value: 48, label: "Every 2 days" },
    { value: 168, label: "Weekly" },
];

function formatTimestamp(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

function formatCurrency(n) {
    if (n == null || Number.isNaN(Number(n))) return "—";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(Number(n));
}

export default function AdminSystemSettingsPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [achTemplates, setAchTemplates] = useState([]);
    const [invoiceTemplates, setInvoiceTemplates] = useState([]);
    const [audit, setAudit] = useState([]);

    // Phase S1 — accordion section open/closed state with localStorage
    // persistence + smart default for first-time visits.
    const { tenant } = useTenant();
    const tenantConfigured =
        !!(tenant && tenant.display_name && tenant.display_name !== "RentFlow");
    const [sectionsOpen, setSectionsOpen] = useState(() => {
        const stored = readSectionStateFromStorage();
        return stored || defaultSectionState(tenantConfigured);
    });
    const toggleSection = (slug) => {
        setSectionsOpen((prev) => {
            const next = { ...prev, [slug]: !prev[slug] };
            writeSectionStateToStorage(next);
            return next;
        });
    };
    const resetSections = () => {
        try { localStorage.removeItem(SECTION_STORAGE_KEY); } catch { /* noop */ }
        setSectionsOpen(defaultSectionState(tenantConfigured));
        toast.message("Layout reset to defaults.");
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, t, ach, inv, a] = await Promise.all([
                api.get("/admin/system-settings"),
                api.get("/admin/templates", {
                    params: { template_type: "rental_agreement", limit: 100, archived: false },
                }),
                api.get("/admin/templates", {
                    params: { template_type: "ach_authorization", limit: 100, archived: false },
                }),
                api.get("/admin/templates", {
                    params: { template_type: "invoice", limit: 100, archived: false },
                }),
                api.get("/admin/system-settings/audit", { params: { limit: 10 } }),
            ]);
            setSettings(s.data);
            setTemplates((t.data?.items || []).filter((x) => !x.archived));
            setAchTemplates((ach.data?.items || []).filter((x) => !x.archived));
            setInvoiceTemplates((inv.data?.items || []).filter((x) => !x.archived));
            setAudit(a.data?.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const patchSetting = async (payload, successMsg) => {
        try {
            const { data } = await api.patch("/admin/system-settings", payload);
            setSettings(data);
            const a = await api.get("/admin/system-settings/audit", { params: { limit: 10 } });
            setAudit(a.data?.items || []);
            toast.success(successMsg || "Setting saved");
            return data;
        } catch (err) {
            toast.error(formatApiError(err));
            throw err;
        }
    };

    return (
        <DashboardShell>
            <div className="fade-up" data-testid="system-settings-page">
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                    Super admin · Configuration
                </p>
                <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-900 ring-1 ring-blue-100">
                        <Settings size={18} />
                    </span>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        System Settings
                    </h1>
                </div>
                <p className="mt-3 max-w-2xl text-sm text-slate-600 leading-relaxed">
                    Tenant-wide defaults for the customer portal. Changes apply to every admin and customer
                    on this account immediately. Each save is captured in the audit log below.
                </p>
                <div className="mt-3 flex items-center justify-end">
                    <button
                        type="button"
                        onClick={resetSections}
                        data-testid="system-reset-layout"
                        className="text-xs text-slate-500 hover:text-blue-900 inline-flex items-center gap-1 underline-offset-2 hover:underline"
                    >
                        <RefreshCcw size={11} />
                        Reset layout
                    </button>
                </div>
            </div>

            <div className="mt-6 space-y-4">
                {/* Section 1: Identity & Branding */}
                <SystemAccordionSection
                    section={SECTION_DEFS[0]}
                    open={!!sectionsOpen["identity-branding"]}
                    onToggle={() => toggleSection("identity-branding")}
                >
                    <TenantSettingsCard />
                    <DefaultTemplateCard
                        settings={settings}
                        templates={templates}
                        loading={loading}
                        onSave={(tplId) =>
                            patchSetting(
                                { default_contract_template_id: tplId || "" },
                                tplId ? "Default contract template saved" : "Default contract template cleared"
                            )
                        }
                        settingKey="default_contract_template_id"
                        archivedKey="default_contract_template_archived"
                        title="Default contract template"
                        description="Used when an admin creates a rental agreement without picking a template explicitly. Admins can still override per-rental."
                        selectLabel="Default rental-agreement template"
                        placeholder="None — admin must pick per rental"
                        emptyHint="No active rental-agreement templates yet."
                        countSuffix="active rental-agreement template(s)."
                        testidPrefix="settings-default-template"
                    />
                    <DefaultTemplateCard
                        settings={settings}
                        templates={achTemplates}
                        loading={loading}
                        onSave={(tplId) =>
                            patchSetting(
                                { default_ach_template_id: tplId || "" },
                                tplId ? "Default ACH template saved" : "Default ACH template cleared"
                            )
                        }
                        settingKey="default_ach_template_id"
                        archivedKey="default_ach_template_archived"
                        title="Default ACH template"
                        description="Used when an admin bundles ACH authorization on a new rental contract without picking a template explicitly. Admins can still override per-rental."
                        selectLabel="Default ACH-authorization template"
                        placeholder="None — admin must pick per rental"
                        emptyHint="No active ACH templates yet."
                        countSuffix="active ACH template(s)."
                        testidPrefix="settings-default-ach-template"
                    />
                    <InvoiceTemplatesCard
                        settings={settings}
                        templates={invoiceTemplates}
                        loading={loading}
                        onSave={(payload, msg) => patchSetting(payload, msg)}
                    />
                </SystemAccordionSection>

                {/* Section 2: Integrations */}
                <SystemAccordionSection
                    section={SECTION_DEFS[1]}
                    open={!!sectionsOpen["integrations"]}
                    onToggle={() => toggleSection("integrations")}
                >
                    <StripeAchCard
                        settings={settings}
                        loading={loading}
                        onSave={(payload) => patchSetting(payload, "Stripe settings saved")}
                    />
                    <EmailProviderCard
                        settings={settings}
                        loading={loading}
                        onReload={load}
                    />
                    <MockEmailCard
                        settings={settings}
                        loading={loading}
                        onSave={(enabled) =>
                            patchSetting(
                                { mock_email_enabled: enabled },
                                enabled ? "Mock email mode ON" : "Mock email mode OFF"
                            )
                        }
                    />
                    <TemplateGeneratorCard
                        settings={settings}
                        loading={loading}
                        onSave={(enabled) =>
                            patchSetting(
                                { template_generator_enabled: enabled },
                                enabled ? "AI generator enabled" : "AI generator disabled",
                            )
                        }
                    />
                    {/* Phase AL0 — Alvys TMS integration (super_admin only).
                        Mounted inside Integrations alongside Stripe & Resend. */}
                    <AlvysSettingsCard />
                    {/* Phase QCM — FMCSA QCMobile webKey (super_admin only). */}
                    {user?.role === "super_admin" && <FmcsaSettingsCard />}
                </SystemAccordionSection>

                {/* Section 3: Notifications & Email */}
                <SystemAccordionSection
                    section={SECTION_DEFS[2]}
                    open={!!sectionsOpen["notifications-email"]}
                    onToggle={() => toggleSection("notifications-email")}
                >
                    <Card className="lg:col-span-2" data-testid="notifications-placeholder-card">
                        <CardHeader>
                            <div className="flex items-center gap-2">
                                <Mail size={16} className="text-blue-900" />
                                <CardTitle className="font-display text-xl">Notification defaults</CardTitle>
                            </div>
                            <CardDescription>
                                Mock-emails outbox + per-tenant notification defaults will land here in
                                a follow-up. For now, notification preferences are managed per-user.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-slate-500">
                                Coming soon. The mock-email store currently captures tenant from + reply-to
                                on every dispatched message — surfaced via the API but not yet through the UI.
                            </p>
                        </CardContent>
                    </Card>
                </SystemAccordionSection>

                {/* Section 4: Audit & Activity */}
                <SystemAccordionSection
                    section={SECTION_DEFS[3]}
                    open={!!sectionsOpen["audit-activity"]}
                    onToggle={() => toggleSection("audit-activity")}
                >
                    <RecentSettingChangesCard />
                    <AuditCard audit={audit} loading={loading} />
                </SystemAccordionSection>

                {/* Section 5: Operational Tools */}
                <SystemAccordionSection
                    section={SECTION_DEFS[4]}
                    open={!!sectionsOpen["operational-tools"]}
                    onToggle={() => toggleSection("operational-tools")}
                >
                    <InvitesCard
                        settings={settings}
                        loading={loading}
                        onSave={(days) =>
                            patchSetting({ default_invite_expiry_days: days }, "Invite expiry saved")
                        }
                    />
                    <SaferCadenceCard
                        settings={settings}
                        loading={loading}
                        onSave={(hours) =>
                            patchSetting({ safer_refresh_hours: hours }, "SAFER cadence updated")
                        }
                    />
                    <CoiDefaultsCard
                        settings={settings}
                        loading={loading}
                        onSave={(payload) => patchSetting(payload, "COI defaults saved")}
                    />
                    <CoiRefreshCard isSuperAdmin={user?.role === "super_admin"} />
                    <InvoiceCronCard
                        settings={settings}
                        loading={loading}
                        onSave={(payload) => patchSetting(payload, "Invoice automation saved")}
                    />
                    <InvoiceCategoriesCard userRole={user?.role} />
                    <DefaultDepositsCard isSuperAdmin={user?.role === "super_admin"} />
                </SystemAccordionSection>

                {/* Section 6 (super_admin only): Danger Zone */}
                {user?.role === "super_admin" && (
                    <SystemAccordionSection
                        section={SECTION_DEFS[5]}
                        open={!!sectionsOpen["danger-zone"]}
                        onToggle={() => toggleSection("danger-zone")}
                    >
                        <div className="lg:col-span-2">
                            <DangerZoneCard />
                        </div>
                    </SystemAccordionSection>
                )}

                {/* Phase PWA — super_admin-only status panel. */}
                {user?.role === "super_admin" && (
                    <div className="lg:col-span-2">
                        <PwaStatusCard />
                    </div>
                )}
            </div>
        </DashboardShell>
    );
}

/* --- Cards --- */

function CardChrome({ children, title, icon: Icon, description, status, testid }) {
    return (
        <Card data-testid={testid}>
            <CardHeader>
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Icon size={16} className="text-blue-900" />
                        <CardTitle className="font-display text-xl">{title}</CardTitle>
                    </div>
                    {status && (
                        <span
                            className={
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset " +
                                (status.tone === "warn"
                                    ? "bg-amber-50 text-amber-800 ring-amber-200"
                                    : "bg-emerald-50 text-emerald-800 ring-emerald-200")
                            }
                            data-testid={`${testid}-status-chip`}
                        >
                            {status.label}
                        </span>
                    )}
                </div>
                {description && <CardDescription>{description}</CardDescription>}
            </CardHeader>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

function InvitesCard({ settings, loading, onSave }) {
    const [days, setDays] = useState("7");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (settings) setDays(String(settings.default_invite_expiry_days ?? 7));
    }, [settings]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        const n = parseInt(days, 10);
        if (!Number.isFinite(n) || n < 1 || n > 90) {
            setErr("Enter a whole number between 1 and 90.");
            return;
        }
        setSaving(true);
        try { await onSave(n); } catch { /* toast already shown */ } finally { setSaving(false); }
    };

    return (
        <CardChrome
            title="Invites"
            icon={Mail}
            description="How long a brand-new invite stays valid before it expires. Per-invite override still wins."
            status={{ label: "Live", tone: "ok" }}
            testid="settings-invites-card"
        >
            <form onSubmit={submit} className="space-y-4 max-w-sm" data-testid="settings-invites-form">
                <div className="space-y-2">
                    <Label htmlFor="default_invite_expiry_days">Default invite expiry (days)</Label>
                    <div className="flex items-center gap-3">
                        <Input
                            id="default_invite_expiry_days"
                            data-testid="settings-invite-expiry-input"
                            type="number"
                            min={1}
                            max={90}
                            step={1}
                            required
                            disabled={loading}
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            className="w-28"
                        />
                        <span className="text-sm text-slate-500">days (1–90)</span>
                    </div>
                </div>
                {err && (
                    <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100" data-testid="settings-invites-error">
                        {err}
                    </div>
                )}
                <div className="flex items-center justify-between gap-4 pt-1">
                    <div className="text-xs text-slate-500">
                        {settings?.updated_at ? <>Last updated {formatTimestamp(settings.updated_at)}</> : <>Using factory default</>}
                    </div>
                    <Button type="submit" disabled={loading || saving} className="bg-blue-900 hover:bg-blue-800" data-testid="settings-invites-save">
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </form>
        </CardChrome>
    );
}

function MockEmailCard({ settings, loading, onSave }) {
    const enabled = settings?.mock_email_enabled !== false;
    const [busy, setBusy] = useState(false);

    const toggle = async (next) => {
        setBusy(true);
        try { await onSave(next); } catch { /* toast already shown */ } finally { setBusy(false); }
    };

    return (
        <CardChrome
            title="Mock email mode"
            icon={Mail}
            description="When ON, all outgoing emails are captured in the in-app Mock Outbox. When OFF, the system attempts a real provider — none is wired yet, so sends will fail with a clear error."
            status={enabled
                ? { label: "Mock ON", tone: "ok" }
                : { label: "Provider mode", tone: "warn" }
            }
            testid="settings-mock-email-card"
        >
            <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                    <div className="text-sm font-medium text-slate-900" data-testid="settings-mock-email-state">
                        {enabled ? "Mock outbox is active" : "Mock outbox is disabled"}
                    </div>
                    <div className="text-xs text-slate-500 leading-relaxed max-w-md">
                        {enabled
                            ? "Invites, contract emails, and COI requests stay inside RentFlow. Safe for staging."
                            : "Outgoing email will hard-fail with a 503 until a provider is configured. Re-enable mock mode if invites stop sending."}
                    </div>
                </div>
                <Switch
                    checked={enabled}
                    disabled={loading || busy}
                    onCheckedChange={toggle}
                    data-testid="settings-mock-email-toggle"
                    aria-label="Toggle mock email mode"
                />
            </div>
            {!enabled && (
                <div
                    className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                    data-testid="settings-mock-email-warning"
                >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>
                        No real provider is configured. Sending an invite right now will return HTTP 503.
                        Re-enable mock mode (or wire SendGrid/Resend in a later phase) before going live.
                    </span>
                </div>
            )}
        </CardChrome>
    );
}

function DefaultTemplateCard({
    settings, templates, loading, onSave,
    settingKey, archivedKey, title, description,
    selectLabel, placeholder, emptyHint, countSuffix, testidPrefix,
}) {
    const [val, setVal] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (settings) setVal(settings[settingKey] || "");
    }, [settings, settingKey]);

    const save = async () => {
        setBusy(true);
        try { await onSave(val); } catch { /* toast already shown */ } finally { setBusy(false); }
    };

    const isArchived = !!settings?.[archivedKey];
    const isSet = !!settings?.[settingKey];
    const statusChip = isArchived
        ? { label: "Archived", tone: "warn" }
        : isSet
            ? { label: "Set", tone: "ok" }
            : { label: "Not set", tone: "warn" };

    return (
        <CardChrome
            title={title}
            icon={FileSignature}
            description={description}
            status={statusChip}
            testid={`${testidPrefix}-card`}
        >
            <div className="space-y-3 max-w-md">
                <Label>{selectLabel}</Label>
                <Select value={val || "__none__"} onValueChange={(v) => setVal(v === "__none__" ? "" : v)} disabled={loading}>
                    <SelectTrigger data-testid={`${testidPrefix}-select`}>
                        <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__none__" data-testid={`${testidPrefix}-option-none`}>
                            {placeholder}
                        </SelectItem>
                        {templates.map((t) => (
                            <SelectItem key={t.id} value={t.id} data-testid={`${testidPrefix}-option-${t.id}`}>
                                {t.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {isArchived && (
                    <div
                        className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                        data-testid={`${testidPrefix}-archived-warning`}
                    >
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <span>
                            <span className="font-semibold">Default template is archived.</span>{" "}
                            Pick a new one (or clear it) — new contracts created without an
                            explicit template will fail until this is fixed.
                        </span>
                    </div>
                )}
                <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-slate-500">
                        {templates.length === 0 ? emptyHint : `${templates.length} ${countSuffix}`}
                    </span>
                    <Button onClick={save} disabled={loading || busy} className="bg-blue-900 hover:bg-blue-800" data-testid={`${testidPrefix}-save`}>
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>
        </CardChrome>
    );
}

function InvoiceTemplatesCard({ settings, templates, loading, onSave }) {
    const [sysRentalVal, setSysRentalVal] = useState("");
    const [adminVal, setAdminVal] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (settings) {
            setSysRentalVal(settings.default_invoice_template_system_rental_id || "");
            setAdminVal(settings.default_invoice_template_admin_id || "");
        }
    }, [settings]);

    const dirty =
        (settings?.default_invoice_template_system_rental_id || "") !== sysRentalVal ||
        (settings?.default_invoice_template_admin_id || "") !== adminVal;

    const save = async () => {
        setBusy(true);
        try {
            await onSave(
                {
                    default_invoice_template_system_rental_id: sysRentalVal || "",
                    default_invoice_template_admin_id: adminVal || "",
                },
                "Invoice template defaults saved",
            );
        } catch {
            /* toast already shown */
        } finally {
            setBusy(false);
        }
    };

    const hasBoth = !!sysRentalVal && !!adminVal;
    const statusChip = hasBoth
        ? { label: "Set", tone: "ok" }
        : (sysRentalVal || adminVal)
            ? { label: "Partial", tone: "warn" }
            : { label: "Not set", tone: "warn" };

    return (
        <CardChrome
            title="Invoice templates"
            icon={Receipt}
            description="Source-aware defaults — the system cron picks one template when it auto-issues monthly rental invoices, and another when an admin creates an invoice manually."
            status={statusChip}
            testid="settings-invoice-templates-card"
        >
            <div className="space-y-5 max-w-md">
                <div className="space-y-2">
                    <Label>System rental (auto-generated)</Label>
                    <Select
                        value={sysRentalVal || "__none__"}
                        onValueChange={(v) => setSysRentalVal(v === "__none__" ? "" : v)}
                        disabled={loading}
                    >
                        <SelectTrigger data-testid="settings-invoice-template-system-rental-select">
                            <SelectValue placeholder="None — fall back to global default" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__" data-testid="settings-invoice-template-system-rental-option-none">
                                None — fall back to global default
                            </SelectItem>
                            {templates.map((t) => (
                                <SelectItem key={t.id} value={t.id} data-testid={`settings-invoice-template-system-rental-option-${t.id}`}>
                                    {t.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                        Used by the monthly cron when rolling up rental charges into customer invoices.
                    </p>
                </div>

                <div className="space-y-2">
                    <Label>Admin-created (manual invoices)</Label>
                    <Select
                        value={adminVal || "__none__"}
                        onValueChange={(v) => setAdminVal(v === "__none__" ? "" : v)}
                        disabled={loading}
                    >
                        <SelectTrigger data-testid="settings-invoice-template-admin-select">
                            <SelectValue placeholder="None — fall back to global default" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__" data-testid="settings-invoice-template-admin-option-none">
                                None — fall back to global default
                            </SelectItem>
                            {templates.map((t) => (
                                <SelectItem key={t.id} value={t.id} data-testid={`settings-invoice-template-admin-option-${t.id}`}>
                                    {t.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                        Used when an admin creates an invoice directly from the dashboard.
                    </p>
                </div>

                <div className="flex items-center justify-between gap-3 pt-1">
                    <span className="text-xs text-slate-500">
                        {templates.length === 0
                            ? "No active invoice templates yet."
                            : `${templates.length} active invoice template(s).`}
                    </span>
                    <Button
                        onClick={save}
                        disabled={loading || busy || !dirty}
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid="settings-invoice-templates-save"
                    >
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>
        </CardChrome>
    );
}

function TemplateGeneratorCard({ settings, loading, onSave }) {
    const enabled = settings?.template_generator_enabled !== false;
    const [busy, setBusy] = useState(false);
    const flip = async () => {
        setBusy(true);
        try { await onSave(!enabled); } catch { /* toast */ } finally { setBusy(false); }
    };
    return (
        <CardChrome
            title="AI invoice template generator"
            icon={Sparkles}
            description="Lets super admins generate invoice template HTML via Claude in the editor. Disable to hide the toolbar button and 503 the endpoint."
            status={enabled ? { label: "Enabled", tone: "ok" } : { label: "Disabled", tone: "warn" }}
            testid="settings-template-generator-card"
        >
            <div className="flex items-center justify-between gap-3 max-w-md">
                <div className="space-y-1">
                    <Label htmlFor="tplgen-toggle" className="cursor-pointer">
                        Generator enabled
                    </Label>
                    <p className="text-xs text-slate-500">
                        Off = no AI calls. Tags + 5/min/admin rate limit are honored when on.
                    </p>
                    <Link
                        to="/admin/settings/audit/template-generator"
                        className="inline-flex items-center gap-1 text-xs text-blue-900 hover:underline pt-1"
                        data-testid="settings-template-generator-audit-link"
                    >
                        View audit log →
                    </Link>
                </div>
                <Switch
                    id="tplgen-toggle"
                    checked={enabled}
                    onCheckedChange={flip}
                    disabled={loading || busy}
                    aria-label="Toggle AI template generator"
                    data-testid="settings-template-generator-toggle"
                />
            </div>
        </CardChrome>
    );
}


function SaferCadenceCard({ settings, loading, onSave }) {
    const [val, setVal] = useState("24");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (settings) setVal(String(settings.safer_refresh_hours ?? 24));
    }, [settings]);

    const save = async () => {
        setBusy(true);
        try { await onSave(parseInt(val, 10)); } catch { /* toast already shown */ } finally { setBusy(false); }
    };

    return (
        <CardChrome
            title="SAFER refresh cadence"
            icon={Truck}
            description="How often FMCSA SAFER snapshots are refreshed for active customers. Lower values give fresher risk flags but more API load."
            status={{ label: `Every ${settings?.safer_refresh_hours ?? 24}h`, tone: "ok" }}
            testid="settings-safer-card"
        >
            <div className="space-y-3 max-w-md">
                <Label>Refresh interval</Label>
                <Select value={val} onValueChange={setVal} disabled={loading}>
                    <SelectTrigger data-testid="settings-safer-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {SAFER_HOUR_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={String(o.value)} data-testid={`settings-safer-option-${o.value}`}>
                                {o.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-slate-500">
                        Job picks up the new value on next tick — no restart needed.
                    </span>
                    <Button onClick={save} disabled={loading || busy} className="bg-blue-900 hover:bg-blue-800" data-testid="settings-safer-save">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>
        </CardChrome>
    );
}

function CoiDefaultsCard({ settings, loading, onSave }) {
    const [required, setRequired] = useState(true);
    const [amount, setAmount] = useState("1000000");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (settings) {
            setRequired(settings.coi_required_by_default !== false);
            setAmount(String(settings.coi_min_liability_amount ?? 1000000));
        }
    }, [settings]);

    const save = async () => {
        setErr("");
        const n = parseInt(amount, 10);
        if (!Number.isFinite(n) || n < 0) {
            setErr("Enter a non-negative dollar amount.");
            return;
        }
        setBusy(true);
        try {
            await onSave({ coi_required_by_default: required, coi_min_liability_amount: n });
        } catch { /* toast already shown */ } finally { setBusy(false); }
    };

    return (
        <CardChrome
            title="COI requirement defaults"
            icon={ShieldCheck}
            description="Applied to brand-new rentals. Admins can still override per rental."
            status={{ label: required ? "Required" : "Optional", tone: required ? "ok" : "warn" }}
            testid="settings-coi-card"
        >
            <div className="space-y-4 max-w-md">
                <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                        <Label>Require COI by default</Label>
                        <p className="text-xs text-slate-500">When OFF, signed contracts skip the COI request and rentals jump straight to active.</p>
                    </div>
                    <Switch
                        checked={required}
                        disabled={loading}
                        onCheckedChange={setRequired}
                        data-testid="settings-coi-required-toggle"
                        aria-label="Toggle COI required by default"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="coi_min_liability_amount">Minimum general-liability amount</Label>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">$</span>
                        <Input
                            id="coi_min_liability_amount"
                            data-testid="settings-coi-amount-input"
                            type="number"
                            min={0}
                            step={50000}
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-44"
                            disabled={loading || !required}
                        />
                        <span className="text-xs text-slate-500">{formatCurrency(amount)}</span>
                    </div>
                </div>
                {err && (
                    <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100">{err}</div>
                )}
                <div className="flex items-center justify-end gap-3">
                    <Button onClick={save} disabled={loading || busy} className="bg-blue-900 hover:bg-blue-800" data-testid="settings-coi-save">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>
        </CardChrome>
    );
}

function InvoiceCronCard({ settings, loading, onSave }) {
    const [beforeDays, setBeforeDays] = useState("7");
    const [onDueDay, setOnDueDay] = useState(true);
    const [overdueList, setOverdueList] = useState("3,7,14");
    const [autoMonthly, setAutoMonthly] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!settings) return;
        setBeforeDays(String(settings.invoice_reminder_before_due_days ?? 7));
        setOnDueDay(settings.invoice_reminder_on_due_day !== false);
        setOverdueList(settings.invoice_reminder_overdue_days_list ?? "3,7,14");
        setAutoMonthly(!!settings.invoice_auto_generate_monthly);
    }, [settings]);

    const save = async () => {
        setErr("");
        const n = parseInt(beforeDays, 10);
        if (Number.isNaN(n) || n < 0 || n > 60) {
            setErr("Days before due must be 0..60 (0 disables)"); return;
        }
        if (overdueList.trim()) {
            for (const tok of overdueList.split(",")) {
                const v = parseInt(tok.trim(), 10);
                if (Number.isNaN(v) || v < 1 || v > 365) {
                    setErr("Overdue days must be comma-separated integers 1..365"); return;
                }
            }
        }
        setSaving(true);
        try {
            await onSave({
                invoice_reminder_before_due_days: n,
                invoice_reminder_on_due_day: onDueDay,
                invoice_reminder_overdue_days_list: overdueList.trim(),
                invoice_auto_generate_monthly: autoMonthly,
            });
        } catch { /* toast shown */ } finally { setSaving(false); }
    };

    return (
        <CardChrome
            title="Invoice automation"
            icon={Receipt}
            description="Reminder cadence + opt-in monthly auto-generation. The auto-monthly cron is OFF by default."
            status={{ label: autoMonthly ? "Auto-monthly ON" : "Auto-monthly OFF", tone: autoMonthly ? "ok" : "warn" }}
            testid="settings-invoice-cron-card"
        >
            <div className="space-y-5 max-w-md">
                <div className="space-y-2">
                    <Label htmlFor="reminder-before-due" className="flex items-center gap-2">
                        <Clock size={14} className="text-slate-500" /> Reminder days before due
                    </Label>
                    <Input id="reminder-before-due" type="number" min={0} max={60} value={beforeDays}
                           onChange={(e) => setBeforeDays(e.target.value)} className="w-28"
                           disabled={loading} data-testid="settings-reminder-before-days" />
                    <p className="text-xs text-slate-500">0 disables the before-due reminder.</p>
                </div>

                <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                        <Label>Send on due day</Label>
                        <p className="text-xs text-slate-500">A nudge on the day the invoice is due.</p>
                    </div>
                    <Switch checked={onDueDay} disabled={loading} onCheckedChange={setOnDueDay}
                            data-testid="settings-reminder-on-due-toggle"
                            aria-label="Send reminder on due day" />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="overdue-days">Overdue follow-up days</Label>
                    <Input id="overdue-days" value={overdueList}
                           onChange={(e) => setOverdueList(e.target.value)}
                           placeholder="3,7,14" disabled={loading}
                           data-testid="settings-overdue-days-list" />
                    <p className="text-xs text-slate-500">Comma-separated days after the due date (empty disables overdue reminders).</p>
                </div>

                <div className="flex items-center justify-between gap-4 pt-3 border-t border-slate-100">
                    <div className="space-y-0.5">
                        <Label className="flex items-center gap-2">
                            <CalendarClock size={14} className="text-slate-500" /> Auto-generate monthly invoices
                        </Label>
                        <p className="text-xs text-slate-500">
                            Runs on the 1st of each month at 01:00 UTC for the prior month. Off by default.
                        </p>
                    </div>
                    <Switch checked={autoMonthly} disabled={loading} onCheckedChange={setAutoMonthly}
                            data-testid="settings-auto-monthly-toggle"
                            aria-label="Toggle auto-monthly invoice cron" />
                </div>

                {err && (
                    <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100">{err}</div>
                )}
                <div className="flex items-center justify-end">
                    <Button onClick={save} disabled={loading || saving}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="settings-invoice-cron-save">
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>

                {/* Phase J.1 Pass 3 — manual cron triggers (dev/QA). */}
                <div className="pt-4 border-t border-dashed border-slate-200 space-y-2"
                     data-testid="settings-invoice-cron-manual">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Manual trigger (dev/QA)</p>
                    <p className="text-xs text-slate-500">
                        Fire the runners once on demand. The auto-monthly trigger bypasses the toggle above.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <ManualCronButton path="/admin/crons/run-reminders" label="Run reminders now"
                                          testid="settings-run-reminders-btn"
                                          confirmMessage="Run the reminder runner once? Idempotency keys prevent duplicate sends, but emails for matching invoices may go out immediately." />
                        <ManualCronButton path="/admin/crons/run-auto-monthly" label="Run auto-monthly now"
                                          testid="settings-run-auto-monthly-btn"
                                          confirmMessage="Run the monthly invoice generator for the previous calendar month? This bypasses the toggle above." />
                    </div>
                </div>
            </div>
        </CardChrome>
    );
}

function ManualCronButton({ path, label, testid, confirmMessage }) {
    const [busy, setBusy] = useState(false);
    const click = async () => {
        if (!window.confirm(confirmMessage)) return;
        setBusy(true);
        try {
            const { data } = await api.post(path);
            const parts = Object.entries(data)
                .filter(([k]) => k !== "billing_month")
                .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`);
            toast.success(`${label.replace(" now", "")} — ${parts.join(" · ")}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };
    return (
        <Button size="sm" variant="outline" onClick={click} disabled={busy} data-testid={testid}
                className="gap-1.5">
            {busy ? "Running…" : label}
        </Button>
    );
}

function StripeAchCard({ settings, loading, onSave }) {
    const [secret, setSecret] = useState("");
    const [pub, setPub] = useState("");
    const [whsec, setWhsec] = useState("");
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!settings) return;
        setPub(settings.stripe_publishable_key || "");
        setSecret("");
        setWhsec("");
    }, [settings]);

    const save = async () => {
        setErr("");
        const payload = {};
        if (secret) payload.stripe_secret_key = secret.trim();
        if (pub !== (settings?.stripe_publishable_key || "")) payload.stripe_publishable_key = pub.trim();
        if (whsec) payload.stripe_webhook_secret = whsec.trim();
        if (Object.keys(payload).length === 0) {
            setErr("Nothing changed — fill at least one field.");
            return;
        }
        setSaving(true);
        try {
            await onSave(payload);
            setSecret(""); setWhsec("");
        } catch { /* toast shown by caller */ } finally { setSaving(false); }
    };

    const testConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/admin/stripe/test-connection");
            setTestResult({ ok: true, ...data });
            toast.success(`Connected to Stripe — ${data.account_id}`);
        } catch (e) {
            const msg = formatApiError(e);
            setTestResult({ ok: false, error: msg });
            toast.error(msg);
        } finally { setTesting(false); }
    };

    const modeTone = settings?.stripe_mode === "live" ? "warn" : "ok";

    return (
        <CardChrome
            title="Stripe (ACH payments)"
            icon={Receipt}
            description="ACH bank-transfer payments via Stripe. Settlement happens only when Stripe's webhook confirms payment_intent.succeeded — never optimistically."
            status={settings?.stripe_enabled
                ? { label: settings?.stripe_mode === "live" ? "Live mode" : "Test mode", tone: modeTone }
                : { label: "Not configured", tone: "warn" }}
            testid="settings-stripe-card"
        >
            <div className="space-y-5 max-w-md">
                <div className="space-y-2">
                    <Label htmlFor="stripe-pk">Publishable key (pk_…)</Label>
                    <Input id="stripe-pk" value={pub} onChange={(e) => setPub(e.target.value)}
                           placeholder="pk_test_..." disabled={loading}
                           data-testid="settings-stripe-pk" autoComplete="off" />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="stripe-sk">Secret key (sk_…)</Label>
                    <Input id="stripe-sk" value={secret} onChange={(e) => setSecret(e.target.value)}
                           placeholder={settings?.stripe_secret_key_masked || "sk_test_..."}
                           type="password" disabled={loading}
                           data-testid="settings-stripe-sk" autoComplete="new-password" />
                    <p className="text-xs text-slate-500">
                        {settings?.stripe_secret_key_masked
                            ? `On file: ${settings.stripe_secret_key_masked}`
                            : "Stored encrypted at rest. Never echoed in API responses."}
                    </p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="stripe-whsec">Webhook signing secret (whsec_…)</Label>
                    <Input id="stripe-whsec" value={whsec} onChange={(e) => setWhsec(e.target.value)}
                           placeholder={settings?.stripe_webhook_secret_masked || "whsec_..."}
                           type="password" disabled={loading}
                           data-testid="settings-stripe-whsec" autoComplete="new-password" />
                    <p className="text-xs text-slate-500">
                        {settings?.stripe_webhook_secret_masked
                            ? `On file: ${settings.stripe_webhook_secret_masked}`
                            : "Register {your-host}/api/stripe/webhook in Stripe Dashboard, then paste the whsec_ here."}
                    </p>
                </div>

                {err && (
                    <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100">{err}</div>
                )}

                <div className="flex items-center justify-between gap-2">
                    <Button onClick={testConnection} disabled={!settings?.stripe_enabled || testing}
                            variant="outline" data-testid="settings-stripe-test"
                            className="gap-1.5">
                        {testing ? "Testing…" : "Test connection"}
                    </Button>
                    <Button onClick={save} disabled={loading || saving}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="settings-stripe-save">
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>

                {testResult && testResult.ok && (
                    <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900 border border-emerald-100"
                         data-testid="settings-stripe-test-result">
                        <p className="font-semibold">Connected as {testResult.display_name || testResult.account_id}</p>
                        <p className="text-xs font-mono text-emerald-700 mt-1">{testResult.account_id} · {testResult.country} · {testResult.mode}</p>
                    </div>
                )}
                {testResult && !testResult.ok && (
                    <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100">
                        {testResult.error}
                    </div>
                )}
            </div>
        </CardChrome>
    );
}


function AuditCard({ audit, loading }) {
    return (
        <Card className="lg:col-span-2" data-testid="settings-audit-card">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <History size={16} className="text-slate-500" />
                    <CardTitle className="font-display text-xl">Recent setting changes</CardTitle>
                </div>
                <CardDescription>Last 10 changes. Captures who changed what and when.</CardDescription>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-sm text-slate-500" data-testid="settings-audit-loading">Loading audit log…</div>
                ) : audit.length === 0 ? (
                    <div className="text-sm text-slate-500" data-testid="settings-audit-empty">No changes yet — saves to settings will appear here.</div>
                ) : (
                    <ul className="space-y-2" data-testid="settings-audit-list">
                        {audit.map((a) => (
                            <li
                                key={a.id}
                                className="rounded-md border border-slate-200 bg-white px-3 py-2"
                                data-testid={`settings-audit-row-${a.id}`}
                            >
                                <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
                                    <span>{formatTimestamp(a.changed_at)}</span>
                                    <span className="font-mono text-[11px]">{a.changed_by_email || a.changed_by}</span>
                                </div>
                                <ul className="mt-1.5 space-y-1">
                                    {(a.changes || []).map((c, idx) => (
                                        <li
                                            key={idx}
                                            className="text-sm flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 min-w-0"
                                            data-testid={`settings-audit-change-${a.id}-${idx}`}
                                        >
                                            <span className="font-medium text-slate-900 break-all">{c.field}</span>
                                            <span className="text-slate-400">·</span>
                                            <span className="font-mono text-xs text-slate-500 break-all min-w-0">{JSON.stringify(c.old)}</span>
                                            <span className="text-slate-400">→</span>
                                            <span className="font-mono text-xs text-slate-900 break-all min-w-0">{JSON.stringify(c.new)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
}

// ──────────────────────────────────────────────────────────────────────────
// Phase I — Email provider (Resend) configuration card.
// ──────────────────────────────────────────────────────────────────────────

function EmailProviderCard({ settings, loading, onReload }) {
    const mockOn = settings?.mock_email_enabled !== false;
    const provider = settings?.email_provider || "mock";
    const masked = settings?.resend_api_key_masked || "";
    const senderStored = settings?.email_sender || "";
    const replyToStored = settings?.email_reply_to || "";

    // Local form state — only show editor when user explicitly Replace/Edit.
    const [editingKey, setEditingKey] = useState(false);
    const [newKey, setNewKey] = useState("");
    const [sender, setSender] = useState("");
    const [replyTo, setReplyTo] = useState("");
    const [saving, setSaving] = useState(false);
    const [testOpen, setTestOpen] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [testing, setTesting] = useState(false);

    useEffect(() => {
        setSender(senderStored);
        setReplyTo(replyToStored);
    }, [senderStored, replyToStored]);

    // Status pill copy + color reflect the three states from the brief.
    const status =
        mockOn
            ? { label: "Mock mode", tone: "amber" }
            : provider === "resend"
                ? { label: "Resend connected", tone: "emerald" }
                : { label: "Mock mode OFF — provider not configured", tone: "red" };

    const toneCls = {
        amber: "bg-amber-100 text-amber-900 border-amber-200",
        emerald: "bg-emerald-100 text-emerald-900 border-emerald-200",
        red: "bg-red-100 text-red-900 border-red-200",
    }[status.tone];

    const patch = async (body, okMsg) => {
        setSaving(true);
        try {
            await api.patch("/admin/system-settings", body);
            toast.success(okMsg);
            await onReload?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const onSaveKey = async () => {
        const k = newKey.trim();
        if (!k) {
            toast.error("Enter a key starting with re_…");
            return;
        }
        await patch({ resend_api_key: k }, "Resend API key saved");
        setEditingKey(false);
        setNewKey("");
    };

    const onClearKey = async () => {
        await patch({ resend_api_key: "" }, "Resend API key cleared");
        setEditingKey(false);
        setNewKey("");
    };

    const onSaveSender = async () => {
        if (sender.trim() === senderStored.trim()) {
            toast.info("No changes to sender");
            return;
        }
        await patch({ email_sender: sender.trim() }, "Sender saved");
    };

    const onSaveReplyTo = async () => {
        if (replyTo.trim() === replyToStored.trim()) {
            toast.info("No changes to reply-to");
            return;
        }
        await patch({ email_reply_to: replyTo.trim() }, "Reply-to saved");
    };

    const onSendTest = async () => {
        const to = testTo.trim();
        if (!to) {
            toast.error("Enter an email address");
            return;
        }
        setTesting(true);
        try {
            const { data } = await api.post("/admin/email-test", { to });
            toast.success(
                `Test sent via ${data.provider}${data.id ? ` (id: ${data.id})` : ""}`
            );
            setTestOpen(false);
            setTestTo("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setTesting(false);
        }
    };

    return (
        <Card data-testid="settings-email-provider-card">
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Send size={18} className="text-blue-900" />
                            Email provider
                        </CardTitle>
                        <CardDescription>
                            Configure Resend for outbound email. The Mock toggle above
                            always wins — when ON, sends still land in the Mock outbox.
                        </CardDescription>
                    </div>
                    <span
                        data-testid="email-provider-status-pill"
                        className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${toneCls}`}
                    >
                        {status.label}
                    </span>
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                {/* Resend API key */}
                <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                        <KeyRound size={14} /> Resend API key
                    </Label>
                    {!editingKey ? (
                        <div className="flex items-center gap-2">
                            <Input
                                data-testid="email-provider-key-display"
                                readOnly
                                value={masked || "Not configured"}
                                className="font-mono"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                disabled={loading || saving}
                                onClick={() => setEditingKey(true)}
                                data-testid="email-provider-key-replace"
                            >
                                {masked ? "Replace key" : "Add key"}
                            </Button>
                            {masked && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="text-red-700 hover:bg-red-50 hover:text-red-800"
                                    disabled={loading || saving}
                                    onClick={onClearKey}
                                    data-testid="email-provider-key-remove"
                                    title="Remove the stored key"
                                >
                                    <Trash2 size={14} />
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Input
                                type="password"
                                placeholder="re_…"
                                value={newKey}
                                onChange={(e) => setNewKey(e.target.value)}
                                className="font-mono"
                                autoFocus
                                data-testid="email-provider-key-input"
                            />
                            <Button
                                type="button"
                                disabled={saving}
                                onClick={onSaveKey}
                                data-testid="email-provider-key-save"
                            >
                                Save key
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                disabled={saving}
                                onClick={() => { setEditingKey(false); setNewKey(""); }}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                    <p className="text-xs text-slate-500">
                        Stored encrypted-at-rest (AES-256-GCM). Only the last 4 chars
                        are ever shown.
                    </p>
                </div>

                {/* Sender */}
                <div className="space-y-2">
                    <Label>Sender</Label>
                    <div className="flex items-center gap-2">
                        <Input
                            placeholder='RentFlow <noreply@yourdomain.com>'
                            value={sender}
                            onChange={(e) => setSender(e.target.value)}
                            data-testid="email-provider-sender-input"
                        />
                        <Button
                            type="button"
                            disabled={loading || saving}
                            onClick={onSaveSender}
                            data-testid="email-provider-sender-save"
                        >
                            Save
                        </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                        Must use a domain you've verified in your Resend dashboard, or
                        use <code className="font-mono">onboarding@resend.dev</code> for
                        testing (delivers only to your verified address).
                    </p>
                </div>

                {/* Reply-to */}
                <div className="space-y-2">
                    <Label>Reply-to <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <div className="flex items-center gap-2">
                        <Input
                            placeholder="support@yourdomain.com"
                            value={replyTo}
                            onChange={(e) => setReplyTo(e.target.value)}
                            data-testid="email-provider-replyto-input"
                        />
                        <Button
                            type="button"
                            disabled={loading || saving}
                            onClick={onSaveReplyTo}
                            data-testid="email-provider-replyto-save"
                        >
                            Save
                        </Button>
                    </div>
                </div>

                {/* Test send */}
                <div className="pt-2 border-t border-slate-100">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setTestOpen(true)}
                        data-testid="email-provider-test-open"
                    >
                        <Send size={14} className="mr-1.5" /> Send test email
                    </Button>
                </div>
            </CardContent>

            <Dialog open={testOpen} onOpenChange={setTestOpen}>
                <DialogContent data-testid="email-provider-test-dialog">
                    <DialogHeader>
                        <DialogTitle>Send a test email</DialogTitle>
                        <DialogDescription>
                            Sends through the currently configured provider
                            (<strong>{mockOn ? "Mock outbox" : provider === "resend" ? "Resend" : "—"}</strong>).
                            With Resend's <code className="font-mono text-[11px]">onboarding@resend.dev</code> sender,
                            delivery is restricted to the API-key owner's verified email.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="email-test-to">Recipient email</Label>
                        <Input
                            id="email-test-to"
                            type="email"
                            placeholder="you@example.com"
                            value={testTo}
                            onChange={(e) => setTestTo(e.target.value)}
                            data-testid="email-provider-test-to"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setTestOpen(false)} disabled={testing}>Cancel</Button>
                        <Button type="button" onClick={onSendTest} disabled={testing} data-testid="email-provider-test-submit">
                            {testing ? "Sending…" : "Send test"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

